/**
 * Files.
 *
 * An asset here is a FILE and nothing else — no rarity, no series, no game
 * configuration. All of that belongs to an Item built from it. stefancoat's
 * asset carried `coatZones`, `patchGridSpan`, `tileMode`, `wallpaperCategory`,
 * `vinylDisplay` and `lightConfig` in one optional-everything bag, so the
 * shape told you nothing about what you were holding.
 *
 * The screen says so: what you see per file is its name, its tags and its
 * dimensions.
 */
import { useRef, useState, type ReactNode } from 'react';
import type { Asset } from '@tomo/contracts';
import { Shell } from '../components/Shell.jsx';
import { Badge, Button, Empty, Failed, Spinner } from '../components/primitives.jsx';
import { Lines } from '../components/form.jsx';
import { ApiError } from '../lib/api.js';
import { assets } from '../lib/tomo.js';
import { useAsync } from '../useAsync.js';
import { useSession } from '../Session.jsx';

export function Assets({ channelId }: { channelId: string }): ReactNode {
  const session = useSession();
  const canEdit = session.channels
    .find((c) => c.id === channelId)?.scopes.includes('assets:write') ?? false;

  const [result, retry] = useAsync((signal) => assets.list(channelId, signal), [channelId]);
  const [tags, setTags] = useState<string[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const input = useRef<HTMLInputElement>(null);

  const upload = (files: FileList | null): void => {
    if (!files || files.length === 0) return;
    setErrors([]);

    // Sequential rather than parallel: a dozen 20MB PUTs at once is how you
    // discover your own upstream limit, and the failure looks like a broken
    // uploader rather than a saturated link.
    void (async () => {
      for (const file of [...files]) {
        setBusy(file.name);
        try {
          await assets.upload(channelId, file, tags);
        } catch (err: unknown) {
          const message = err instanceof ApiError ? err.message : 'Could not upload that.';
          setErrors((all) => [...all, `${file.name}: ${message}`]);
        }
      }
      setBusy(null);
      retry();
    })();
  };

  return (
    <Shell channelId={channelId}>
      <h1 className="text-xl font-semibold tracking-tight">Files</h1>
      <p className="mt-1 text-sm text-ink-400">
        Artwork, and nothing else. What a collectible <em>is</em> — its name, its rarity,
        how many exist — belongs to an item built from these.
      </p>

      {canEdit ? (
        <div className="mt-8 max-w-lg space-y-3">
          <label className="block">
            <span className="block text-sm text-ink-200">Tag everything in this batch</span>
            <span className="mt-0.5 block text-xs text-ink-400">
              One per line. Tags are how a reward finds a pool later.
            </span>
            <span className="mt-1.5 block">
              <Lines value={tags} onChange={setTags} placeholder={'weapon\nseason-one'} />
            </span>
          </label>

          <input
            ref={input}
            type="file"
            multiple
            // Only what tomo stores. The server checks the bytes regardless —
            // this just avoids an obviously pointless round trip.
            accept="image/png,image/jpeg,image/gif,image/webp"
            className="hidden"
            onChange={(e) => { upload(e.target.files); e.target.value = ''; }}
          />
          <Button onClick={() => input.current?.click()} disabled={busy !== null}>
            {busy ? `Uploading ${busy}…` : 'Choose files'}
          </Button>
        </div>
      ) : null}

      {errors.length > 0 ? (
        <div className="mt-6 max-w-lg space-y-2">
          {errors.map((e) => <Failed key={e} message={e} />)}
        </div>
      ) : null}

      <div className="mt-10">
        {result.state === 'loading' ? <Spinner label="Loading your files…" /> : null}
        {result.state === 'failed' ? (
          <Failed message="Could not load your files." detail={result.error.message} onRetry={retry} />
        ) : null}
        {result.state === 'ready' && result.value.length === 0 ? (
          <Empty
            title="No files yet."
            hint="Upload the artwork first. Everything else is built from it."
          />
        ) : null}
        {result.state === 'ready' && result.value.length > 0 ? (
          <ul className="grid gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {result.value.map((asset) => <Card key={asset.id} asset={asset} />)}
          </ul>
        ) : null}
      </div>
    </Shell>
  );
}

function Card({ asset }: { asset: Asset }): ReactNode {
  return (
    <li className="overflow-hidden rounded-lg border border-ink-800">
      <span className="grid aspect-square place-items-center bg-ink-900">
        {asset.url && asset.status === 'active' ? (
          <img
            src={asset.url}
            alt={asset.name}
            loading="lazy"
            className="size-full object-contain"
          />
        ) : (
          <span className="text-xs text-ink-700">
            {asset.status === 'pending' ? 'uploading…' : 'no preview'}
          </span>
        )}
      </span>
      <span className="block p-3">
        <span className="block truncate text-sm text-ink-50">{asset.name}</span>
        <span className="mt-1 flex flex-wrap items-center gap-1.5">
          {asset.status !== 'active' ? <Badge tone="warn">{asset.status}</Badge> : null}
          {asset.width && asset.height ? (
            <Badge>{asset.width}×{asset.height}</Badge>
          ) : null}
          {asset.tags.slice(0, 3).map((t) => <Badge key={t} tone="brand">#{t}</Badge>)}
        </span>
      </span>
    </li>
  );
}
